// src/components/answer/AnswerText.jsx

import React from "react";
import { InlineMath } from "react-katex";
import "katex/dist/katex.min.css";

/**
 * AnswerText
 *
 * Renders normal answer text:
 * - paragraphs
 * - bullets
 * - numbered lists
 * - headings like Findings / Follow-up as plain styled section labels
 * - citation markers like [1], [2]
 * - inline LaTeX math such as \(x = 5\) without interfering with citations
 *
 * This file does NOT handle sources, tables, display math blocks, or charts.
 */

function isBulletLine(line) {
  return /^\s*[-•]\s+/.test(line);
}

function isNumberedLine(line) {
  return /^\s*\d+[.)]\s+/.test(line);
}

function isPlainSectionHeading(line) {
  return /^(Findings|Follow[-\s]?up|Clarifying question|One clarifying question|Notes|Important|Summary|Key points?|Next steps?|Recommendations?|Conclusion)\s*:?\s*$/i.test(
    String(line ?? "").trim()
  );
}


const INLINE_MATH_FILE_EXTENSION_RE =
  /\.(?:pdf|docx?|xlsx?|pptx?|csv|txt|rtf|odt|ods|odp|pages|numbers|key|json|xml|html?|md|zip|png|jpe?g|gif|webp|heic|heif|svg)\b/i;

const INLINE_MATH_URL_RE =
  /https?:\/\//i;

const INLINE_MATH_EMAIL_RE =
  /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;

const INLINE_MATH_LATEX_COMMAND_RE =
  /\\(?:frac|dfrac|tfrac|sqrt|sum|prod|int|lim|log|ln|sin|cos|tan|exp|alpha|beta|gamma|delta|Delta|theta|lambda|mu|sigma|Sigma|phi|omega|Omega|pi|rho|infty|partial|nabla|cdot|times|div|pm|leq?|geq?|neq|approx)\b/;

const INLINE_MATH_RELATION_RE =
  /(?:=|≈|≠|≤|≥|<=|>=|\\leq?|\\geq?|\\neq|\\approx)/;

const INLINE_MATH_OPERATOR_RE =
  /(?:\+|[×÷·]|\\times\b|\\cdot\b|\\div\b|\*|\/)/;

const INLINE_MATH_POWER_OR_SUBSCRIPT_RE =
  /(?:\^\{[^}]+\}|\^[-+]?[A-Za-z0-9()]|_\{[^}]+\}|\b[A-Za-z]{1,5}_[A-Za-z0-9]{1,6}\b)/;

const INLINE_MATH_FUNCTION_RE =
  /\b(?:f|g|h|P|PV|FV|NPV|IRR|ROI|APR|r|x|y|z|sin|cos|tan|log|ln|exp|sqrt|max|min)\s*\([^)]*\)/i;

const INLINE_MATH_SYMBOL_RE =
  /[∑∫√∞πθλμσΔΣΩρφ∇±∓∂]/;

function isSafeCitationNumber(value) {
  const text = String(value || "").trim();

  if (!/^\d{1,3}$/.test(text)) {
    return false;
  }

  const number = Number(text);

  return (
    Number.isInteger(number) &&
    number >= 1 &&
    number <= 999
  );
}

function looksLikeInlineMath(latex) {
  const text = String(latex || "").trim();
  if (!text) return false;

  /*
    \(...\) is already explicit math syntax, so the threshold here should be
    permissive enough not to block real math. The guard only rejects obvious
    non-math payloads and plain prose accidentally wrapped in delimiters.
  */
  if (INLINE_MATH_URL_RE.test(text)) return false;
  if (INLINE_MATH_EMAIL_RE.test(text)) return false;
  if (INLINE_MATH_FILE_EXTENSION_RE.test(text)) return false;

  if (INLINE_MATH_LATEX_COMMAND_RE.test(text)) return true;
  if (INLINE_MATH_SYMBOL_RE.test(text)) return true;
  if (INLINE_MATH_FUNCTION_RE.test(text)) return true;
  if (INLINE_MATH_POWER_OR_SUBSCRIPT_RE.test(text)) return true;

  const hasRelation =
    INLINE_MATH_RELATION_RE.test(text);

  const hasOperator =
    INLINE_MATH_OPERATOR_RE.test(text);

  const hasDigit =
    /\d/.test(text);

  const hasMathVariable =
    /\b[A-Za-z]\b/.test(text);

  if (
    hasRelation &&
    (
      hasOperator ||
      hasDigit ||
      hasMathVariable
    )
  ) {
    return true;
  }

  if (
    hasOperator &&
    (
      hasDigit ||
      hasMathVariable
    )
  ) {
    return true;
  }

  // A single variable/constant explicitly wrapped in \( ... \) is valid math.
  if (/^[A-Za-z0-9]+$/.test(text)) {
    return true;
  }

  return false;
}

function renderInlineMathFallback(latex, key) {
  return (
    <span
      key={key}
      className="answer-inline-math-fallback"
    >
      {`\\(${String(latex || "")}\\)`}
    </span>
  );
}

function SafeInlineMath({ latex }) {
  const value =
    String(latex || "").trim();

  if (
    !value ||
    !looksLikeInlineMath(value)
  ) {
    return (
      <span className="answer-inline-math-fallback">
        {`\\(${value}\\)`}
      </span>
    );
  }

  try {
    return (
      <InlineMath
        math={value}
        errorColor="#dc2626"
        renderError={() => (
          <span className="answer-inline-math-fallback">
            {`\\(${value}\\)`}
          </span>
        )}
      />
    );
  } catch {
    return (
      <span className="answer-inline-math-fallback">
        {`\\(${value}\\)`}
      </span>
    );
  }
}

function renderInlineText(text, keyPrefix = "inline") {
  const raw = String(text || "");
  const parts = [];

  /*
    Only explicit \( ... \) is eligible for inline math.
    Single-dollar parsing is intentionally forbidden because legal, tax,
    accounting, and financial answers frequently contain ordinary currency.
  */
  const tokenRe =
    /\\\(([\s\S]+?)\\\)|\[([0-9]{1,4})\]/g;

  let lastIndex = 0;
  let match;
  let idx = 0;

  while (
    (match = tokenRe.exec(raw)) !== null
  ) {
    const before =
      raw.slice(
        lastIndex,
        match.index
      );

    if (before) {
      parts.push(before);
    }

    if (match[1] !== undefined) {
      const latex =
        match[1].trim();

      const mathKey =
        `${keyPrefix}-math-${idx}`;

      if (looksLikeInlineMath(latex)) {
        parts.push(
          <SafeInlineMath
            key={mathKey}
            latex={latex}
          />
        );
      } else {
        parts.push(
          renderInlineMathFallback(
            latex,
            mathKey
          )
        );
      }
    } else if (
      match[2] !== undefined
    ) {
      const citation =
        match[2];

      if (
        isSafeCitationNumber(
          citation
        )
      ) {
        parts.push(
          <sup
            key={`${keyPrefix}-cite-${idx}`}
            className="answer-citation-marker"
          >
            [{citation}]
          </sup>
        );
      } else {
        // Preserve non-source bracketed numbers, such as [2026], as text.
        parts.push(
          match[0]
        );
      }
    }

    lastIndex =
      tokenRe.lastIndex;

    idx += 1;
  }

  const after =
    raw.slice(lastIndex);

  if (after) {
    parts.push(after);
  }

  return parts;
}

function normalizeLine(line) {
  let value =
    String(line ?? "")
      .replace(
        /^\s{0,3}#{1,6}\s*/,
        ""
      )
      .trimEnd();

  /*
    Preserve internal ** and __ characters. Removing them globally can mutate
    filenames, identifiers, and ordinary document text. Only unwrap markdown
    emphasis when the entire line is deliberately wrapped.
  */
  value = value.replace(
    /^\s*\*\*(.+)\*\*\s*$/,
    "$1"
  );

  value = value.replace(
    /^\s*__(.+)__\s*$/,
    "$1"
  );

  return value;
}

export default function AnswerText({ value }) {
  const text = String(value || "").trim();
  if (!text) return null;

  const lines = text.split(/\r?\n/).map(normalizeLine);

  const groups = [];
  let current = [];

  function flush() {
    if (current.length) {
      groups.push(current);
      current = [];
    }
  }

  for (const line of lines) {
    if (!line.trim()) {
      flush();
      continue;
    }

    current.push(line);
  }

  flush();

  return (
    <div className="answer-text">
      {groups.map((group, groupIndex) => {
        if (group.length === 1 && isPlainSectionHeading(group[0])) {
          return (
            <div key={`heading-${groupIndex}`} className="answer-section-label">
              {group[0].replace(/:$/, "")}
            </div>
          );
        }

        const allBullets = group.every((line) => isBulletLine(line));
        const allNumbered = group.every((line) => isNumberedLine(line));

        if (allBullets) {
          return (
            <ul key={`ul-${groupIndex}`} className="answer-list">
              {group.map((line, idx) => {
                const cleaned = line.replace(/^\s*[-•]\s+/, "");
                return (
                  <li key={`li-${idx}`}>
                    {renderInlineText(
                      cleaned,
                      `ul-${groupIndex}-${idx}`
                    )}
                  </li>
                );
              })}
            </ul>
          );
        }

        if (allNumbered) {
          return (
            <ol key={`ol-${groupIndex}`} className="answer-list answer-numbered-list">
              {group.map((line, idx) => {
                const cleaned = line.replace(/^\s*\d+[.)]\s+/, "");
                return (
                  <li key={`oli-${idx}`}>
                    {renderInlineText(
                      cleaned,
                      `ol-${groupIndex}-${idx}`
                    )}
                  </li>
                );
              })}
            </ol>
          );
        }

        return (
          <div key={`pgroup-${groupIndex}`} className="answer-paragraph-group">
            {group.map((line, idx) => {
              if (isPlainSectionHeading(line)) {
                return (
                  <div key={`section-${idx}`} className="answer-section-label">
                    {line.replace(/:$/, "")}
                  </div>
                );
              }

              return (
                <p key={`p-${idx}`} className="answer-paragraph">
                  {renderInlineText(line, `p-${groupIndex}-${idx}`)}
                </p>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}
